import React, { useMemo, useState } from 'react';
import { Reorder, AnimatePresence, motion } from 'framer-motion';
import { DraggableTab } from './DraggableTab';
import { HydratedPost, PostType, Post, FollowerGroup } from '../types';
import { PostCreator } from './PostCreator';
import { PostGrid } from './PostGrid';
import { stickerTextStyle } from './StickerText';
import { PlusIcon } from './Icons';

export const HomePage = ({
    posts,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToProfile,
    onNavigateToHashtag,
    onCreatePost,
    homeFeedMode,
    onHomeFeedModeChange,
    onVouch,
    onMuteUser,
    onBlockUser,
    onGrantAdmin,
    onDeleteUser,
    onReportPost,
    onSwipeTransition,
    onShare,
    onNavigateToCommunity,
    isModalOpen = false,
    isDemo = false,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    postBlurOpacity,
    gridColumns = 1,
    onGridColumnsChange,
    preferredScale = 1,
    onScaleChange,
    initialVisibleCount,
    onVisibleCountChange,
    onInternalModalStateChange,
    closeInternalModalsTrigger = 0,
    isLoading,
    initialSharedContent,
    clearSharedContent,
    followerGroups = [],
    appTextColor = '#ffffff',
    onUploadFailed,
    failedPosts = [],
    onRetryPostUpload,
    tabsOrder,
    onReorderTabs,
    onAddToCart,
    isShakingMode = false,
    postcardGap = 8,
    onPostcardGapChange
}: {
    posts: HydratedPost[],
    onPostClick: (post: HydratedPost) => void,
    onLike: (postId: number) => void,
    onBump: (post: HydratedPost) => void,
    onBookmark: (postId: number) => void,
    onEdit: (post: HydratedPost) => void,
    onDelete: (postId: number) => void,
    onNavigateToProfile: (userId: string) => void,
    onNavigateToHashtag: (tag: string) => void,
    onNavigateToCommunity?: (communityId: string, channelId?: string) => void,
    onCreatePost: (postData: {
        type: PostType,
        content: Post['content'],
        visibility?: 'public' | 'followers' | 'groups' | 'private',
        visibilityGroups?: string[],
        showCaption?: boolean,
        showOnProfile?: boolean,
        textSize?: 'xs' | 'sm' | 'med' | 'lg',
        communityId?: string,
        themeOpacity?: number,
        themeColor?: string,
        textColor?: string,
        isOptimistic?: boolean,
        localId?: number,
        fontId?: string,
        postcardStyle?: string,
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall'
    }) => void,
    homeFeedMode: string,
    onHomeFeedModeChange: (mode: string) => void,
    onVouch?: (postId: number) => void,
    onMuteUser?: (userId: string) => void,
    onBlockUser?: (userId: string) => void,
    onGrantAdmin?: (userId: string) => void,
    onDeleteUser?: (userId: string) => void,
    onReportPost?: (postId: number) => void,
    onSwipeTransition?: (direction: 'left' | 'right') => void,
    onShare?: (post: HydratedPost, slideIndex?: number) => void,
    isModalOpen?: boolean,
    isDemo?: boolean,
    animatingLikes: Set<number>,
    animatingBumps: Set<number>,
    animatingBookmarks: Set<number>,
    galleryAnimation: string,
    postBlurOpacity: number,
    gridColumns?: 1 | 2 | 3 | 4 | 5 | 6,
    onGridColumnsChange?: (cols: 1 | 2 | 3 | 4 | 5 | 6) => void,
    preferredScale?: number,
    onScaleChange?: (scale: number) => void,
    initialVisibleCount?: number,
    onVisibleCountChange?: (count: number) => void,
    onInternalModalStateChange?: (isOpen: boolean) => void,
    closeInternalModalsTrigger?: number,
    isLoading?: boolean,
    initialSharedContent?: { text: string, media: any[], sourcePostId?: number } | null,
    clearSharedContent?: () => void,
    followerGroups?: FollowerGroup[],
    appTextColor?: string,
    onUploadFailed?: (localId: number) => void,
    failedPosts?: HydratedPost[],
    onRetryPostUpload?: (localId: number, startUpload: any) => void,
    tabsOrder?: string[],
    onReorderTabs?: (newOrder: string[]) => void,
    onAddToCart?: (post: Post) => void,
    isShakingMode?: boolean,
    postcardGap?: number,
    onPostcardGapChange?: (gap: number) => void
}) => {
    const combinedPosts = useMemo(() => {
        const union = [...failedPosts, ...posts.filter(p => !failedPosts.some(fp => fp.id === p.id || (fp as any).localId === (p as any).localId))];
        return union.sort((a, b) => {
            if (a.uploadFailed && !b.uploadFailed) return -1;
            if (!a.uploadFailed && b.uploadFailed) return 1;
            return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        });
    }, [posts, failedPosts]);

    const tabs = useMemo(() => {
        const baseTabs = [
            { id: 'all', name: 'All' },
            { id: 'following', name: 'Following' },
            ...followerGroups.map(g => {
                let display = g.name;
                if (g.id === 'default_all' && display.toLowerCase().startsWith('all ')) {
                    display = display.substring(4);
                    display = display.charAt(0).toUpperCase() + display.slice(1);
                }
                return { id: g.id, name: display };
            })
        ];

        if (!tabsOrder || tabsOrder.length === 0) return baseTabs;

        const orderedTabs: typeof baseTabs = [];
        const baseTabsMap = new Map(baseTabs.map(t => [t.id, t]));

        tabsOrder.forEach(id => {
            if (baseTabsMap.has(id)) {
                orderedTabs.push(baseTabsMap.get(id)!);
                baseTabsMap.delete(id);
            }
        });

        baseTabsMap.forEach(t => orderedTabs.push(t));
        return orderedTabs;
    }, [followerGroups, tabsOrder]);

    const [isReorderMode, setIsReorderMode] = useState(false);
    const [isPostCreatorVisible, setIsPostCreatorVisible] = useState(false);

    return (
        <div className="flex flex-col min-h-screen w-full">
            {/* Header Logo - Sticky Banner */}
            <div className="sticky top-0 z-30 w-full pt-[calc(0.75rem+env(safe-area-inset-top,0px))] pb-3">
                <div className="w-[90%] max-w-[600px] mx-auto flex justify-center">
                    <img src="/assets/goodlyfe_star.png" alt="GoodLyfe" className="w-full max-h-10 sm:max-h-12 object-contain" />
                </div>
            </div>
            {/* Post Creator Section */}
            <div className="w-full pt-4 flex flex-col items-center">
                <button 
                    onClick={() => setIsPostCreatorVisible(!isPostCreatorVisible)}
                    className="mb-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex items-center gap-2"
                    style={{ color: appTextColor, ...stickerTextStyle(0.18) }}
                >
                    <PlusIcon className={`w-4 h-4 transition-transform ${isPostCreatorVisible ? 'rotate-45 text-red-400' : 'text-blue-400'}`} />
                    {isPostCreatorVisible ? 'Hide Composer' : 'Quick Compose'}
                </button>
                <AnimatePresence>
                    {isPostCreatorVisible && (
                        <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="w-full overflow-hidden"
                        >
                            <PostCreator
                                onCreatePost={onCreatePost}
                                postBlurOpacity={postBlurOpacity}
                                onInternalModalStateChange={onInternalModalStateChange}
                                closeInternalModalsTrigger={closeInternalModalsTrigger}
                                initialSharedContent={initialSharedContent}
                                clearSharedContent={clearSharedContent}
                                onUploadFailed={onUploadFailed}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Feed Navigation Tabs */}
            {isReorderMode && (
                <div 
                    className="fixed inset-0 z-40 bg-transparent" 
                    onClick={() => setIsReorderMode(false)}
                    onTouchStart={() => setIsReorderMode(false)}
                />
            )}
            <div className={`w-full px-4 lg:px-[5%] py-2 ${isReorderMode ? 'relative z-50' : ''}`}>
                <nav
                    className="flex space-x-6 overflow-x-auto no-scrollbar py-1"
                    onTouchStart={(e) => e.stopPropagation()}
                    style={{ touchAction: isReorderMode ? 'none' : 'pan-x' }}
                >
                    <Reorder.Group 
                        axis="x" 
                        values={tabs.map(t => t.id)} 
                        onReorder={(newOrder) => onReorderTabs && onReorderTabs(newOrder)} 
                        className="flex space-x-6 h-full"
                        style={{ overflow: 'visible' }}
                    >
                        {tabs.map(tab => (
                            <DraggableTab
                                key={tab.id}
                                item={tab.id}
                                label={tab.name}
                                isActive={homeFeedMode === tab.id}
                                onClick={() => onHomeFeedModeChange(tab.id)}
                                color={appTextColor}
                                isReorderMode={isReorderMode}
                                onEnterReorderMode={() => setIsReorderMode(true)}
                                onExitReorderMode={() => setIsReorderMode(false)}
                            />
                        ))}
                    </Reorder.Group>
                    {!isReorderMode && (
                        <button
                            onClick={() => window.dispatchEvent(new CustomEvent('app.openFollowerGroups'))}
                            style={{
                                color: appTextColor,
                                borderColor: 'transparent',
                                ...stickerTextStyle(0.18)
                            }}
                            className="py-3 px-2 inline-flex items-center justify-center transition-all duration-200 border-b-2 border-transparent hover:opacity-100 hover:border-gray-700 opacity-60 flex-shrink-0"
                            title="Edit Follower Groups"
                            aria-label="Edit Follower Groups"
                        >
                            <PlusIcon className="w-4 h-4" />
                        </button>
                    )}
                </nav>
            </div>

            <PostGrid
                posts={combinedPosts}
                onPostClick={onPostClick}
                onLike={onLike}
                onBump={onBump}
                onBookmark={onBookmark}
                onEdit={onEdit}
                onDelete={onDelete}
                onNavigateToProfile={onNavigateToProfile}
                onNavigateToHashtag={onNavigateToHashtag}
                onVouch={onVouch}
                onMuteUser={onMuteUser}
                onBlockUser={onBlockUser}
                onGrantAdmin={onGrantAdmin}
                onDeleteUser={onDeleteUser}
                onReportPost={onReportPost}
                animatingLikes={animatingLikes}
                animatingBumps={animatingBumps}
                animatingBookmarks={animatingBookmarks}
                galleryAnimation={galleryAnimation}
                postBlurOpacity={postBlurOpacity}
                preferredColumns={gridColumns}
                onColumnsChange={onGridColumnsChange}
                preferredScale={preferredScale}
                onScaleChange={onScaleChange}
                onSwipeTransition={onSwipeTransition}
                onShare={onShare}
                onNavigateToCommunity={onNavigateToCommunity}
                isModalOpen={isModalOpen}
                onAddToCart={onAddToCart}
                isLoading={isLoading}
                showEndMessage={true}
                initialVisibleCount={initialVisibleCount}
                onVisibleCountChange={onVisibleCountChange}
                isHome={true}
                onRetryPostUpload={onRetryPostUpload}
                isShakingMode={isShakingMode}
                postcardGap={postcardGap}
                onPostcardGapChange={onPostcardGapChange}
            />
        </div>
    );
};
